/* ==========================================================================
   Metrics Overview Component
   Task F37: Stat cards for high-level product health metrics
   ========================================================================== */

import { Building2, Clock, Users, FileText, DollarSign } from 'lucide-react'
import type { OverviewMetrics } from '@/api/internal'

const CURRENCY_SYMBOLS: Record<string, string> = { usd: '$', cad: 'CA$' }

interface StatCardProps {
  label: string
  value: string
  /** Extra figures shown under the value, e.g. non-USD revenue. */
  subValues?: string[]
  icon: React.ComponentType<{ className?: string }>
}

function StatCard({ label, value, subValues, icon: Icon }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-md bg-soft-lavender">
          <Icon className="h-5 w-5 text-deep-purple" />
        </div>
      </div>
      <p className="text-2xl font-bold text-charcoal">{value}</p>
      {subValues && subValues.length > 0 && (
        <div className="mt-0.5 space-y-0.5">
          {subValues.map((sv) => (
            <p key={sv} className="text-sm font-semibold text-charcoal/70">
              {sv}
            </p>
          ))}
        </div>
      )}
      <p className="text-sm text-charcoal/60 mt-1">{label}</p>
    </div>
  )
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-5 animate-pulse">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-9 w-9 rounded-md bg-gray-200" />
      </div>
      <div className="h-8 w-20 bg-gray-200 rounded mb-2" />
      <div className="h-4 w-32 bg-gray-100 rounded" />
    </div>
  )
}

interface MetricsOverviewProps {
  data: OverviewMetrics | null
  loading: boolean
}

export function MetricsOverview({ data, loading }: MetricsOverviewProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    )
  }

  if (!data) return null

  const formatRevenue = (v: number, currency: string) =>
    `${CURRENCY_SYMBOLS[currency] ?? '$'}${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  // Currencies are reported side by side, never summed — there is no FX rate.
  // Zero buckets are hidden so an all-USD book renders exactly as before.
  const revenueEntries = (data.revenue_by_currency ?? []).filter(
    (c) => c.amount > 0
  )
  const primaryRevenue = revenueEntries[0] ?? {
    currency: 'usd',
    amount: data.total_revenue_this_month,
  }
  const otherRevenue = revenueEntries.slice(1)

  const formatDays = (v: number | null) =>
    v !== null ? `${v.toFixed(1)} days` : '--'

  const formatAvg = (v: number | null) =>
    v !== null ? v.toFixed(1) : '--'

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      <StatCard
        label="Live Portals"
        value={String(data.total_portals_live)}
        icon={Building2}
      />
      <StatCard
        label="Avg Days to Go-Live"
        value={formatDays(data.avg_days_to_golive)}
        icon={Clock}
      />
      <StatCard
        label="Active Providers (30d)"
        value={formatAvg(data.active_providers_at_day30)}
        icon={Users}
      />
      <StatCard
        label="Scripts This Month"
        value={String(data.total_scripts_this_month)}
        icon={FileText}
      />
      <StatCard
        label="Revenue This Month"
        value={formatRevenue(primaryRevenue.amount, primaryRevenue.currency)}
        subValues={otherRevenue.map((c) => formatRevenue(c.amount, c.currency))}
        icon={DollarSign}
      />
    </div>
  )
}
